import {
    type Dispatch,
    type ReactNode,
    type SetStateAction,
    createContext,
    useCallback,
    useContext,
    useState,
    useEffect
} from "react";

import { useGoogleReCaptcha } from "react-google-recaptcha-v3";
import { FormattedMessage, useIntl } from "react-intl";
import { v4 as createUUID } from "uuid";

import { useAnalyticsEvents } from "@atlaskit/analytics-next";
import { Platform } from "@atlassiansox/asc-stargate";

import {
    SignupGetUFOErrorType,
    SignupUFOLoggingActionTimeoutError,
    trackAndCompleteUFOEvent,
    ufoEventExistingSiteN2ESubmit,
    ufoEventGraphQLWebhookSetup,
    ufoEventNewSiteN2NSubmit,
    UfoEventName,
    UfoExperienceStatus,
    UfoInteractionType,
    UfoLabelStack,
    operateWithSignupUFOLogging,
    trackSignupStarted,
    trackSiteNameChangedFromRecommendedSiteName,
    trackSitePickerSubmit,
    trackSubmitSuccess,
    getProductData,
    mapProductKeyAndEditionToCcpProduct,
    ProductData,
    ProductDataDetail,
    ProductKey,
    getConfirmationPageUrl,
    getCrossFlowActivationUrl,
    getRecaptcha,
    getRedirectUrlFromShadowItError,
    getSignupContext,
    isTestTenantByName,
    trackRecaptchaFailure
} from "@/components/signup/utils";
import { useUserContext } from "@/components/user-context";
import {
    AisSitesType,
    UserSite,
    useBxpWebStargateClient
} from "@/libraries/bxp-web-stargate-client/src/hook";
import { useLocalizedHref } from "@/libraries/web-framework";
import { Cookie } from "@/utils/cookie";
import { useLocation } from "@/utils/location-provider";
import { LoggedInUser, useLoggedInUser } from "@/utils/logged-in-user-provider";

import { messages as validationMessages } from "./messages";

export type FormattedMessageProps = React.ComponentProps<
    typeof FormattedMessage
>;

const { ABORTED, SUCCEEDED } = UfoExperienceStatus;
const { PRESS, TRANSITION } = UfoInteractionType;
const ufoBaseLogs: UfoLabelStack = [
    { name: "pkg:bxp-ui" },
    { name: "component:SiteNameContextProvider" }
];

export interface SiteNameContext {
    activateSite: () => void;
    orchestrationId?: string;
    isNewUser?: boolean;
    isSubmittingState: [boolean, Dispatch<SetStateAction<boolean>>];
    productData: ProductDataDetail;
    recommendedSiteNameValueState: [string, Dispatch<SetStateAction<string>>];
    showSitePickerState: [boolean, Dispatch<SetStateAction<boolean>>];
    siteNameValidState: [boolean, Dispatch<SetStateAction<boolean>>];
    siteNameValueState: [string, Dispatch<SetStateAction<string>>];
    sitePickerValueState: [
        AisSitesType | UserSite | undefined,
        Dispatch<SetStateAction<AisSitesType | UserSite | undefined>>
    ];
    userInfo: LoggedInUser;
    validationMessageState: [
        FormattedMessageProps | undefined,
        Dispatch<SetStateAction<FormattedMessageProps | undefined>>
    ];
}

export const SiteNameContext = createContext<SiteNameContext | null>(null);

export const useSiteNameContext = (): SiteNameContext => {
    const context = useContext(SiteNameContext);
    if (!context) {
        throw new Error(
            "useSiteNameContext must be used within a SiteNameContextProvider"
        );
    }
    return context;
};

export const useSiteNameValue = () => {
    const {
        siteNameValueState: [siteNameValue]
    } = useSiteNameContext();
    return siteNameValue;
};

export const useSetSiteNameValue = () => {
    const {
        siteNameValueState: [, setSiteNameValue]
    } = useSiteNameContext();
    return setSiteNameValue;
};

export const SiteNameContextProvider = ({
    children,
    productKey,
    isNewUser
}: {
    children: ReactNode;
    productKey: string;
    isNewUser?: boolean;
}) => {
    const { executeRecaptcha } = useGoogleReCaptcha();
    const { createAnalyticsEvent } = useAnalyticsEvents();
    const { edition = "free", profile } = useUserContext();
    const [showSitePicker, setShowSitePicker] = useState(false);
    const siteNameValidState = useState(false);
    const [siteNameValid] = siteNameValidState;
    const isSubmittingState = useState(false);
    const [, setIsSubmitting] = isSubmittingState;
    const siteNameValueState = useState("");
    const [sitePickerValue, setSitePickerValue] = useState<
        AisSitesType | UserSite | undefined
    >();
    const [siteName] = siteNameValueState;
    const recommendedSiteNameValueState = useState("");
    const [recommendedSiteName] = recommendedSiteNameValueState;
    const [recaptchaFailedToLoad, setRecaptchFailedToLoad] =
        useState<boolean>(false);
    const [
        recaptchaRetryTimeRemainingSecond,
        setRecaptchaRetryTimeRemainingSecond
    ] = useState(60);

    const [validatationMessage, setValidationMessage] = useState<
        undefined | FormattedMessageProps
    >();
    const location = useLocation();
    const productData = getProductData(productKey as keyof typeof ProductData);
    const signupClient = useBxpWebStargateClient();
    const searchParameters = new URLSearchParams(location.search);
    const platform = (searchParameters.get("platform") ??
        "routing") as Platform;
    const primaryCollectionProduct = searchParameters.get(
        "primaryCollectionProduct"
    ) as ProductKey | undefined;

    const userInfo = useLoggedInUser();
    const intl = useIntl();
    const localizedContactUrl = useLocalizedHref("/company/contact");

    // eslint-disable-next-line  @typescript-eslint/no-explicit-any
    const redirectToAdminHubForShadowIt = (error: any) => {
        const errorCode = error?.response?.status;
        const redirectUrlShadowIt =
            errorCode === 403 && getRedirectUrlFromShadowItError(error);

        // Shadow IT unauthorized user redirects to adminhub's Shadow IT request flow.
        if (errorCode === 403 && Boolean(redirectUrlShadowIt)) {
            window.location.assign(redirectUrlShadowIt as string);
        }
    };

    const showRecaptchaFormError = useCallback(() => {
        const anonymousId = decodeURIComponent(
            Cookie.readCookie("ajs_anonymous_id") || "n/a"
        );
        setValidationMessage({
            ...validationMessages.timeout,
            values: {
                // eslint-disable-next-line react/no-unstable-nested-components
                "href-support": (node: ReactNode[]) => (
                    <a href={localizedContactUrl}>{node}</a>
                ),
                "error_id": `Anonymous ID: ${anonymousId}`
            }
        });

        // Reset form to allow re-submit
        setIsSubmitting(false);
    }, [localizedContactUrl]);

    // TODO: Jest does not test useCallback and we will need Enzyme integrated to test coverage
    /* istanbul ignore next */
    const activateSiteHandler = useCallback(
        async (signupId?: string) => {
            setIsSubmitting(true);

            if (!executeRecaptcha && recaptchaRetryTimeRemainingSecond > 0) {
                // set trigger that will wait for Recaptcha retry logic
                setRecaptchFailedToLoad(true);
                return;
            }
            setRecaptchFailedToLoad(false);

            const localeData = await signupClient.getConsentConfig();

            const testingPurposes = isTestTenantByName(siteName);
            const signupContext = getSignupContext({
                productKey,
                testingPurposes,
                signupId,
                search: location.search
            });

            const MAX_SIGNUP_CONTEXT_LENGTH = 8192;
            let signupContextString: string = JSON.stringify(signupContext);
            if (signupContextString.length > MAX_SIGNUP_CONTEXT_LENGTH) {
                signupContextString = "{}";
            }
            const ccpProducts = mapProductKeyAndEditionToCcpProduct({
                productKey: productKey as ProductKey,
                edition
            });

            const gRecaptchaResponse = await getRecaptcha(
                userInfo.email || "",
                executeRecaptcha
            );

            ufoEventNewSiteN2NSubmit.setCustomData({
                gRecaptchaResponse: gRecaptchaResponse ?? "none",
                executeRecaptchaExists: Boolean(executeRecaptcha),
                reactComponent: "SiteNameContextProvider"
            });

            // Handle missing reCAPTCHA response
            if (!gRecaptchaResponse) {
                trackRecaptchaFailure(
                    createAnalyticsEvent,
                    "bxp-ui-site-name-form"
                );
                showRecaptchaFormError();
                return;
            }

            // Set experiment metadata before starting UFO event for GraphQL webhook setup
            ufoEventGraphQLWebhookSetup.setCustomData({
                webPlatform: "bxp-ui"
            });

            const response = await signupClient.activateProduct({
                displayName: userInfo.name,
                email: userInfo.email || "",
                ccpProducts: [ccpProducts],
                siteName,
                timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
                useCcpPlatform: platform === "ccp",
                consent: getConsents(
                    localeData.locale ?? "US-VA",
                    localeData.localeRequiresMarketingCommunicationOptIn
                ),
                platform,
                signupContext: signupContextString,
                isNewUser: productKey === ProductKey.JIRA_SOFTWARE || isNewUser,
                gRecaptchaResponse,
                testingPurposes,
                signupId
            });

            const redirectUrl = getConfirmationPageUrl({
                locale: intl.locale,
                productKey,
                response,
                siteName,
                signupId,
                edition,
                primaryCollectionProduct
            });
            window.location.assign(redirectUrl);
        },
        [
            edition,
            executeRecaptcha,
            recaptchaRetryTimeRemainingSecond,
            showRecaptchaFormError,
            intl,
            isNewUser,
            localizedContactUrl,
            platform,
            productKey,
            setIsSubmitting,
            setValidationMessage,
            signupClient,
            siteName,
            userInfo
        ]
    );

    useEffect(() => {
        const ufoSubmitEvent = showSitePicker
            ? ufoEventExistingSiteN2ESubmit
            : ufoEventNewSiteN2NSubmit;

        if (recaptchaFailedToLoad && recaptchaRetryTimeRemainingSecond <= 0) {
            ufoSubmitEvent.failure({
                customData: {
                    reactComponent: "SiteNameContextProvider",
                    analyticsEvent: "errored",
                    recaptchaRetryTimeRemainingSecond: 0,
                    siteType: showSitePicker ? "existing-site" : "new-site"
                },
                error: new Error("Recaptcha retry failed")
            });
        }
        if (recaptchaFailedToLoad) {
            activateSiteHandler()
                .then(() => {
                    ufoSubmitEvent.success({
                        customData: {
                            reactComponent: "SiteNameContextProvider",
                            analyticsEvent: "succeeded",
                            siteType: showSitePicker
                                ? "existing-site"
                                : "new-site",
                            recaptchaRetryTimeRemainingSecond
                        }
                    });
                })
                .catch((error) => {
                    showRecaptchaFormError();
                    ufoSubmitEvent.failure({
                        customData: {
                            reactComponent: "SiteNameContextProvider",
                            analyticsEvent: "errored",
                            siteType: showSitePicker
                                ? "existing-site"
                                : "new-site",
                            recaptchaRetryTimeRemainingSecond
                        },
                        error
                    });
                    return;
                });
        }
    }, [
        executeRecaptcha,
        localizedContactUrl,
        recaptchaFailedToLoad,
        recaptchaRetryTimeRemainingSecond,
        showRecaptchaFormError
    ]);

    useEffect(() => {
        if (!recaptchaFailedToLoad) return;
        if (recaptchaRetryTimeRemainingSecond <= 0) return;
        const intervalId = setInterval(() => {
            setRecaptchaRetryTimeRemainingSecond(
                recaptchaRetryTimeRemainingSecond - 1
            );
        }, 1000);
        return () => clearInterval(intervalId);
    }, [recaptchaFailedToLoad, recaptchaRetryTimeRemainingSecond]);

    const activateSite = async () => {
        sessionStorage.setItem("provisioningStartTime", `${Date.now()}`);
        // Start UFOs to begin duration metrics for signup submit
        ufoEventNewSiteN2NSubmit.start({
            productKey: productKey as ProductKey,
            webPlatform: "bxp-ui"
        });
        ufoEventExistingSiteN2ESubmit.start({
            productKey: productKey as ProductKey,
            webPlatform: "bxp-ui"
        });
        const ufoLogs: UfoLabelStack = [
            ...ufoBaseLogs,
            { name: "method:activateSite" }
        ];

        // user must be logged in
        if (!userInfo.isUserLoggedIn) return;

        // Generate a signup ID and add it to all the events in this flow.
        // This id is used to stitch together a view of the end-to-end
        // provisioning experience, all the way to landing in product.
        const signupId = createUUID();
        const isN2N = !showSitePicker && !sitePickerValue;
        const domainType = profile && profile.cohorts?.domainType;
        trackSignupStarted(
            createAnalyticsEvent,
            signupId,
            productKey,
            edition,
            isN2N,
            domainType
        );

        // handle site picker submit
        if (showSitePicker) {
            if (sitePickerValue) {
                try {
                    // Check for Shadow IT authorization.
                    await signupClient.getActivationAuthorization({
                        productKeys: [productKey],
                        productEdition: edition as string,
                        cloudId: sitePickerValue.cloudId,
                        siteName: sitePickerValue?.displayName
                    });

                    const crossFlowActivationUrl = getCrossFlowActivationUrl({
                        cloudId: sitePickerValue.cloudId,
                        edition,
                        productKey: productKey as ProductKey,
                        siteName: sitePickerValue?.displayName
                    });

                    ufoEventExistingSiteN2ESubmit.success({
                        customData: {
                            reactComponent: "SiteNameContextProvider",
                            analyticsEvent: "succeeded",
                            siteType: "existing-site"
                        }
                    });

                    trackSitePickerSubmit({
                        createAnalyticsEvent,
                        targetCloudId: sitePickerValue.cloudId,
                        provisionedProducts: [{ product: productKey, edition }],
                        signupUrl: crossFlowActivationUrl,
                        aaid: userInfo.accountId,
                        componentSource: "SiteNameContextProvider"
                    });

                    window.location.assign(crossFlowActivationUrl);
                } catch (error) {
                    ufoEventExistingSiteN2ESubmit.failure({
                        customData: {
                            reactComponent: "SiteNameContextProvider",
                            analyticsEvent: "errored",
                            siteType: "existing-site"
                        },
                        error
                    });

                    redirectToAdminHubForShadowIt(error);
                }
            }
            return;
        }

        if (!siteNameValid || !siteName) return;

        trackSubmitSuccess(createAnalyticsEvent);

        if (siteName !== recommendedSiteName) {
            trackSiteNameChangedFromRecommendedSiteName(
                createAnalyticsEvent,
                siteName,
                recommendedSiteName
            );
        }

        let setTimeoutId: ReturnType<typeof setTimeout>;

        const interactionId = createUUID();
        operateWithSignupUFOLogging(
            {
                interactionId,
                interactionName: "try-cloud-signup_form-submit",
                type: "press",
                timeout: 2 * 60 * 1000,
                labelStack: [{ name: "bxp-ui" }],
                errorContext: "error occured on signup form submit button"
            },
            async () =>
                Promise.race([
                    new Promise((_resolve, reject) => {
                        setTimeoutId = setTimeout(() => {
                            reject(SignupUFOLoggingActionTimeoutError);
                        }, 60000);
                    }),
                    activateSiteHandler(signupId).finally(() => {
                        if (setTimeoutId) {
                            clearTimeout(setTimeoutId);
                        }
                    })
                ])
        )
            .then(() => {
                // SLO "good event" for N2N signup submit
                ufoEventNewSiteN2NSubmit.success({
                    customData: {
                        reactComponent: "SiteNameContextProvider",
                        analyticsEvent: "succeeded",
                        siteType: "new-site"
                    }
                });

                trackAndCompleteUFOEvent({
                    experienceName: UfoEventName.ACTIVATE_PRODUCT_API_SUCCESS,
                    experienceStatus: SUCCEEDED,
                    interactionType: TRANSITION,
                    ufoLogger: ufoLogs
                });

                // Reliabiltiy SLO "good event" for signup submit captured on bxp-ui only
                trackAndCompleteUFOEvent({
                    experienceName:
                        UfoEventName.BXPUI_SIGNUP_SUBMIT_SLO_SUCCEEDED,
                    experienceStatus: SUCCEEDED,
                    interactionType: PRESS,
                    ufoLogger: ufoLogs
                });

                // Reliabiltiy SLO "good event" for signup submit captured across bxp-ui and bxpkit
                trackAndCompleteUFOEvent({
                    experienceName:
                        UfoEventName.DEFAULT_SIGNUP_SUBMIT_SLO_SUCCEEDED,
                    experienceStatus: SUCCEEDED,
                    interactionType: PRESS,
                    ufoLogger: ufoLogs
                });
            })
            .catch((error) => {
                // SLO "bad event" for N2N signup submit
                ufoEventNewSiteN2NSubmit.failure({
                    customData: {
                        reactComponent: "SiteNameContextProvider",
                        analyticsEvent: "errored",
                        siteType: "new-site"
                    },
                    error
                });

                redirectToAdminHubForShadowIt(error);

                const traceId = error?.response?.headers?.get("atl-traceid");

                setValidationMessage({
                    ...validationMessages.timeout,
                    values: {
                        // eslint-disable-next-line react/no-unstable-nested-components
                        "href-support": (node) => (
                            <a href={localizedContactUrl}>{node}</a>
                        ),
                        "error_id": traceId
                            ? `Trace ID: ${traceId}`
                            : `Interaction ID: ${interactionId}`
                    }
                });

                // don't re-enable the form when there is an HTTP exception. this helps with rate-limiting
                const errorType = SignupGetUFOErrorType(error);
                if (errorType !== "http_exception") {
                    // Reset form to allow re-submit
                    setIsSubmitting(false);
                }

                trackAndCompleteUFOEvent({
                    experienceName: UfoEventName.ACTIVATE_PRODUCT_API_ERROR,
                    experienceStatus: ABORTED,
                    interactionType: TRANSITION,
                    error,
                    errorName: "ActivateProductError",
                    errorMessage: "error occured on activateProduct",
                    ufoLogger: ufoLogs
                });

                // Reliabiltiy SLO "bad event" for signup submit captured on bxp-ui only
                trackAndCompleteUFOEvent({
                    experienceName: UfoEventName.BXPUI_SIGNUP_SUBMIT_SLO_FAILED,
                    experienceStatus: ABORTED,
                    interactionType: PRESS,
                    error,
                    errorName: "ActivateProductError",
                    errorMessage: "error occured on activateProduct",
                    ufoLogger: ufoLogs
                });

                // Reliabiltiy SLO "bad event" for signup submit captured across bxp-ui and bxpkit
                trackAndCompleteUFOEvent({
                    experienceName:
                        UfoEventName.DEFAULT_SIGNUP_SUBMIT_SLO_FAILED,
                    experienceStatus: ABORTED,
                    interactionType: PRESS,
                    error,
                    errorName: "ActivateProductError",
                    errorMessage: "error occured on activateProduct",
                    ufoLogger: ufoLogs
                });
            });
    };

    const getConsents = (
        locale: string,
        localeRequiresMarketingCommunicationOptIn?: boolean
    ) => {
        const tosAndPrivacyMessage = intl.formatMessage(
            {
                id: "BxpUiSignup.nameSite.consent.tosAndPrivacy",
                defaultMessage:
                    "<p>By clicking below, you agree to the Atlassian <termsOfServiceLink>Cloud Terms of Service</termsOfServiceLink> and <privacyPolicyLink>Privacy Policy.</privacyPolicyLink></p>"
            },
            {
                p: (text) => `<p>${text}</p>`,
                termsOfServiceLink: (text) =>
                    `<a href="https://www.atlassian.com/legal/cloud-terms-of-service">${text}</a>`,
                privacyPolicyLink: (text) =>
                    `<a href="https://www.atlassian.com/legal/privacy-policy">${text}</a>`
            }
        );

        const generalConsentDisabledMessage = intl.formatMessage({
            id: "BxpUiSignup.nameSite.consent.generalConsentDisabledMessage",
            defaultMessage: "general marketing consent disabled"
        });

        const generalMarketingOptInDefaultMessage = intl.formatMessage({
            id: "BxpUiSignup.nameSite.consent.generalMarketingOptInDefaultMessage",
            defaultMessage:
                "Yes! I would like to receive promotional emails including product news, events and more from Atlassian and its affiliates."
        });

        const consent = {
            site: "atlassian",
            locale: locale,
            formUrl: location.href,
            consents: [
                {
                    key: "termsOfService",
                    displayedText: tosAndPrivacyMessage,
                    granted: true
                },
                {
                    key: "privacyPolicy",
                    displayedText: tosAndPrivacyMessage,
                    granted: true
                },
                {
                    key: "generalMarketingOptIn",
                    displayedText: generalMarketingOptInDefaultMessage,
                    granted: false
                }
            ]
        };
        if (localeRequiresMarketingCommunicationOptIn) {
            //@ts-ignore
            consent.consents[2].granted = false;
            //@ts-ignore
            consent.consents[2].displayedText = generalConsentDisabledMessage;
        } else {
            //@ts-ignore
            consent.consents[2].granted = true;
        }
        return consent;
    };

    return (
        <SiteNameContext.Provider
            value={{
                activateSite,
                isNewUser,
                isSubmittingState,
                productData,
                recommendedSiteNameValueState,
                showSitePickerState: [showSitePicker, setShowSitePicker],
                siteNameValidState,
                siteNameValueState,
                sitePickerValueState: [sitePickerValue, setSitePickerValue],
                userInfo,
                validationMessageState: [
                    validatationMessage,
                    setValidationMessage
                ]
            }}
        >
            {children}
        </SiteNameContext.Provider>
    );
};
